import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import TableLoader from "Components/Common/Loader/TableLoader";
import Pagination from "Components/Pagination/Pagination";
import KycPopup from "./KycPopup";
import { MdDeleteForever, MdCancel } from "react-icons/md";
import { HiSearch } from "react-icons/hi";
import ConfirmDeleteModal from "./ConfirmDeleteModal";
import Swal from "sweetalert2";
import { usePermission } from "Context/PermissionContext";
import { loadDecrypted } from "Common Usage/localStorageUtils";
const UserKyc = () => {
  const [loading, setLoading] = useState(false);
  const [kycData, setKycData] = useState([]);
  const [activeTab, setActiveTab] = useState("On-Process");
  const [kycPopupOpen, setKycPopupOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 5;
  const [totalPages, setTotalPages] = useState(1);

  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [deleteUserId, setDeleteUserId] = useState(null);
  const [deleteUser, setDeleteUser] = useState(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const { permissions } = usePermission();
  const currentTabPermissions =
    permissions?.payout?.subModule?.kyc?.permissions;

  const loggedInUser = loadDecrypted("HeartConnect-StaffData");
  const staffData = Array.isArray(loggedInUser)
    ? loggedInUser[0]
    : loggedInUser;
  const staffId = staffData?._id || staffData?.id || "";

  //  Debounce search
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuery(searchQuery);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  const getKycData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/payout-kyc/status`,
        {
          params: {
            page: currentPage,
            limit: rowsPerPage,
            status: activeTab.toUpperCase(),
            searchQuery: debouncedQuery.trim(),
          },
        }
      );

      const { data, total, totalPages } = response?.data || {};
      setKycData(data || []);
      setTotalPages(totalPages || 1);

      // setStatistics({
      //   total: total || 0,
      //   approved: data?.filter((d) => d.status === "APPROVED").length || 0,
      //   pending: data?.filter((d) => d.status === "PENDING").length || 0,
      // });
    } catch (error) {
      console.error("KYC Fetch Error:", error.message);
    } finally {
      setLoading(false);
    }
  }, [activeTab, currentPage, debouncedQuery]);

  useEffect(() => {
    getKycData();
  }, [getKycData]);

  const createStaffActivityLog = async (actionType, userData) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/staffs-activity-log/create-staffs-activity`,
        {
          staff: staffId,
          action: actionType,
          description: `KYC deleted for user ${
            userData?.firstName
              ? `${userData.firstName} ${userData.lastName}`
              : userData?.panName || ""
          } (Phone: ${userData?.phonenumber || "-"})`,
        }
      );
    } catch (error) {
      console.error("Activity log failed:", error.message);
    }
  };

  const handleDeleteUser = async () => {
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_API_URL}/payout-kyc/delete/${deleteUserId}`
      );
      await createStaffActivityLog("DELETE_KYC", deleteUser);

      Swal.fire({
        icon: "success",
        title: "Success!",
        text: response.data?.message || "Deleted successfully.",
        confirmButtonColor: "#c51369",
        // timer: 2000,
      });

      getKycData();
    } catch (err) {
      console.error("Delete Error:", err);
      const errorMessage =
        err.response?.data?.message || "Something went wrong while deleting.";

      Swal.fire({
        icon: "error",
        title: "Error!",
        text: errorMessage,
        confirmButtonColor: "#c51369",
      });
    } finally {
      setConfirmModalOpen(false);
      setDeleteUserId(null);
      setDeleteUser(null);
    }
  };

  return (
    <>
      <div className="py-5">
        <div className="mx-auto my-1 w-auto rounded-t-2xl">
          {/* Tabs + Search Box */}
          <div className="mb-4 flex flex-col items-start justify-between gap-4 rounded-lg bg-white p-4 md:flex-row md:items-center">
            {/* Tabs */}
            <div className="flex flex-wrap gap-2 sm:gap-4">
              {["On-Process", "Pending", "Approved", "Rejected"].map((tab) => (
                <button
                  key={tab}
                  onClick={() => {
                    setActiveTab(tab);
                    setCurrentPage(1);
                  }}
                  className={`rounded-lg px-3 py-2 text-sm sm:px-4 sm:text-base ${
                    activeTab === tab
                      ? "bg-[#c51369] text-white"
                      : "bg-gray-200 text-gray-700"
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>

            {/*  Search Box */}
            <div className="relative flex w-full items-center rounded-2xl shadow-sm md:w-2/5">
              <HiSearch className="absolute left-4 text-gray-500" />
              <input
                type="text"
                placeholder="Search by phone number..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full rounded-md border border-gray-400 px-10 py-2 text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#c51369]"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery("")}
                  className="absolute right-4 text-gray-500 hover:text-gray-700"
                >
                  <MdCancel />
                </button>
              )}
            </div>
          </div>

          {/* Table Section */}
          {loading ? (
            <TableLoader />
          ) : (
            <div className="overflow-x-auto rounded-t-2xl">
              <table className="w-full min-w-[700px] text-left text-sm sm:text-base">
                <thead className="bg-[#e0ced7] text-xs uppercase tracking-wide text-navy-900 sm:text-sm">
                  <tr className="text-center">
                    <th className="px-2 py-3 text-center sm:px-4">Sl</th>
                    <th className="px-2 py-3 text-center sm:px-4">Date</th>
                    <th className="px-2 py-3 text-center sm:px-4">User</th>
                    <th className="px-2 py-3 text-center sm:px-4">
                      Verified Name
                    </th>
                    <th className="px-2 py-3 text-center sm:px-4">Status</th>
                    <th className="px-2 py-3 text-center sm:px-4">Action</th>
                  </tr>
                </thead>

                <tbody>
                  {kycData.length > 0 ? (
                    kycData.map((row, index) => (
                      <tr
                        key={row.userId}
                        className="cursor-pointer bg-white text-xs text-gray-700 transition-colors hover:bg-gray-100 sm:text-sm"
                        onClick={() => {
                          setSelectedUser(row);
                          setKycPopupOpen(true);
                        }}
                      >
                        <td className="border px-2 py-3 text-center sm:px-4">
                          {index + 1 + (currentPage - 1) * rowsPerPage}
                        </td>
                        <td className="border px-2 py-3 text-center sm:px-4">
                          {row?.date
                            ? new Date(row.date).toLocaleDateString()
                            : "-"}
                        </td>
                        <td className="border px-2 py-3 text-center sm:px-4">
                          <div className="flex flex-col items-center justify-center gap-1">
                            <span>
                              {row?.firstName
                                ? `${row.firstName} ${row.lastName}`
                                : row?.panName}
                            </span>
                            <span>Phone - {row?.phonenumber || "-"}</span>
                            <span>UPI ID - {row?.upiId || "-"}</span>
                          </div>
                        </td>
                        <td className="border px-2 py-3 text-center sm:px-4">
                          <div className="flex flex-col items-center">
                            <span className="font-medium text-gray-600">
                              PAN: {row?.panName || "-"}
                            </span>
                            <span className="text-gray-600">
                              UPI: {row?.upiName || "-"}
                            </span>
                          </div>
                        </td>
                        <td className="border px-2 py-3 text-center sm:px-4">
                          <span
                            className={`rounded-lg p-1 text-[10px] sm:p-2 sm:text-xs ${
                              row?.status === "PENDING"
                                ? "bg-orange-100 text-orange-500"
                                : row?.status === "REJECTED"
                                ? "bg-red-100 text-red-500"
                                : row?.status === "ON-PROCESS"
                                ? "bg-blue-100 text-blue-500"
                                : "bg-green-100 text-green-500"
                            }`}
                          >
                            {row?.status}
                          </span>
                        </td>
                        <td className="border px-2 py-3 text-center sm:px-4">
                          {currentTabPermissions?.delete && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteUserId(row.userId);
                                setDeleteUser(row);
                                setConfirmModalOpen(true);
                              }}
                            >
                              <MdDeleteForever
                                size={26}
                                className="text-red-500 hover:text-red-700"
                              />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={6}
                        className="py-6 text-center text-gray-500"
                      >
                        {searchQuery.trim()
                          ? "No records found matching your search"
                          : `No ${activeTab} data found`}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          <div className="mt-4 flex justify-center">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={(page) => setCurrentPage(page)}
            />
          </div>
        </div>
      </div>

      {/* Popup & Delete Modal */}
      <KycPopup
        open={kycPopupOpen}
        onClose={() => setKycPopupOpen(false)}
        user={selectedUser}
        refreshData={getKycData}
        currentTabPermissions={currentTabPermissions}
        staffId={staffId}
      />

      <ConfirmDeleteModal
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={handleDeleteUser}
        user={deleteUser}
      />
    </>
  );
};

export default UserKyc;
